import { html, render } from "htm/preact";
import { createRef } from "preact";

import { BibleReader, type BibleReaderProps } from "../components/BibleReader";
import {
  BibleProgressContext,
  type BibleContentSettings,
  defaultSettings as bibleContentDefaultSettings,
} from "../components/BibleContent";
import { getVerseRects } from "../components/BibleContentVerse";
import { Providers } from "../components/Providers";
import { themeChanged } from "../components/ThemeProvider";
import { APIClient } from "../modules/api";
import { Content as BibleContent } from "../modules/bible";
import { dispatchEvent, setHostNode } from "../modules/events";
import { type EventMap } from "../modules/events";
import { parseIntNullable } from "../modules/helpers";
import { Progress } from "../modules/progress";

export class BibleReaderElement extends HTMLElement {
  #contentRef = createRef<BibleContent>();

  #isHydrating = false;

  // Progress

  #progress = new Progress();

  get __unstableProgress() {
    return this.#progress;
  }

  // Query

  #query: string | undefined;

  get query() {
    return this.#query;
  }

  set query(value) {
    if (!value) return;
    this.#query = value;
    this.#renderRequest();
  }

  // Render

  #renderFrame: number = -1;

  get #renderProps(): BibleReaderProps | null {
    if (this.#isHydrating) return null;
    const { query, settings, translation } = this;
    if (!query) {
      console.warn(
        `Warning: <bible-reader> missing "query" attribute/property`,
      );
      return null;
    }
    return {
      contentRef: this.#contentRef,
      query,
      settings,
      translation,
    };
  }

  // Settings

  #settings: Partial<BibleContentSettings> = {};

  get settings(): Partial<BibleContentSettings> {
    return this.#settings;
  }

  set settings(value) {
    this.#settings = value;
    this.#renderRequest();
  }

  // Translation

  #translation: number = 1;

  get translation() {
    return this.#translation;
  }

  set translation(value) {
    this.#translation = value;
    this.#renderRequest();
  }

  constructor() {
    super();
    const template = this.querySelector("template");
    // Declarative Shadow DOM polyfill detected
    if (template) {
      this.attachShadow({ mode: "open" });
      this.shadowRoot?.append(template.content);
      template.remove();
    }
    // Declarative Shadow DOM (either via browser API or polyfill)
    // already attached the ShadowRoot
    if (this.shadowRoot) {
      this.#isHydrating = true;
    } else {
      this.attachShadow({ mode: "open" });
      // Declarative Shadow DOM will include a <style> tag,
      // so this is only needed for client-side use cases
      import("./bible-reader.css?inline").then(({ default: css }) => {
        const node = document.createElement("style");
        node.innerHTML = css;
        this.shadowRoot?.appendChild(node);
      });
    }
    setHostNode(this);
  }

  attributeChangedCallback(
    name: string,
    _: string | null,
    value: string | null,
  ) {
    switch (name) {
      case "query":
        if (value) {
          this.query = value;
        }
        break;
      case "translation": {
        const translation = parseTranslation(value);
        if (translation) {
          this.translation = translation;
        }
        break;
      }
      default:
        if (name.startsWith(SETTINGS_ATTRIBUTE_PREFIX)) {
          const key = settingKeyFromAttributeName(name);
          this.#settings = {
            ...this.#settings,
            [key]: settingsAttributeValue(key, value),
          };
        }
    }
    this.#renderRequest();
  }

  connectedCallback() {
    if (this.#isHydrating) {
      const query = this.getAttribute("query");
      const translation = parseTranslation(this.getAttribute("translation"));
      if (query && translation) {
        APIClient.shared.bibleQuery(query, translation).then((payload) => {
          this.#contentRef.current = new BibleContent(payload);
          this.#isHydrating = false;
          this.#renderRequest();
          dispatchEvent("ready", {});
        });
      }
    } else {
      this.#renderRequest();
      dispatchEvent("ready", {});
    }
  }

  getVerse(id: number) {
    return this.#contentRef.current?.verses.get(id);
  }

  getVerseRects() {
    return getVerseRects(this.shadowRoot!);
  }

  themeChanged() {
    themeChanged();
  }

  #render() {
    const props = this.#renderProps;
    if (!props) return;
    render(
      html`
        <${Providers}>
          <${BibleProgressContext.Provider} value=${this.#progress}>
            <${BibleReader} ...${props} />
          <//>
        <//>
      `,
      // @ts-ignore
      this.shadowRoot,
    );
  }

  #renderRequest() {
    cancelAnimationFrame(this.#renderFrame);
    this.#renderFrame = requestAnimationFrame(() => {
      this.#render();
    });
  }

  static get observedAttributes() {
    return ["query", "translation"].concat(
      Object.keys(bibleContentDefaultSettings).map((key) =>
        settingAttributeNameFromKey(key as keyof BibleContentSettings),
      ),
    );
  }
}

function parseTranslation(value: string | null) {
  return parseIntNullable(value ?? "1");
}

const SETTINGS_ATTRIBUTE_PREFIX = "setting-";

function settingAttributeNameFromKey(name: keyof BibleContentSettings) {
  return (
    SETTINGS_ATTRIBUTE_PREFIX +
    name.replace(/([A-Z])/g, (_, letter: string) => "-" + letter.toLowerCase())
  );
}

function settingsAttributeValue<
  K extends keyof BibleContentSettings,
  V = BibleContentSettings[K],
>(key: K, value: string | null): V {
  if (typeof bibleContentDefaultSettings[key] === "boolean") {
    return (value === null ? false : true) as V;
  }
  return value as V;
}

function settingKeyFromAttributeName(name: string) {
  return name
    .slice(SETTINGS_ATTRIBUTE_PREFIX.length)
    .replace(/-([a-z])/g, (_, letter: string) =>
      letter.toUpperCase(),
    ) as keyof BibleContentSettings;
}

type BibleReaderEventMap = {
  [K in keyof EventMap]: CustomEvent<EventMap[K]>;
};

interface CombinedEventMap extends HTMLElementEventMap, BibleReaderEventMap {}

export interface BibleReaderElement {
  addEventListener<K extends keyof CombinedEventMap>(
    type: K,
    listener: (this: BibleReaderElement, event: CombinedEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener<K extends keyof CombinedEventMap>(
    type: K,
    listener: (this: BibleReaderElement, event: CombinedEventMap[K]) => void,
  ): void;
}

declare global {
  interface HTMLElementTagNameMap {
    "bible-reader": BibleReaderElement;
  }
}

customElements.define("bible-reader", BibleReaderElement);
